import { useState, useCallback } from 'react';
import { shouldShowAtlasChartIntegration } from 'src/utils/app';
import {
  advisorContextService,
  buildAdvisorUrl,
  extractAdvisorFilterIds,
  CreateContextRequest,
  AttachmentInfo
} from 'src/services/advisorContext';
import { useAppSelector } from 'src/utils/Hooks';
import { useUserEmail } from 'src/utils/Hooks/reduxSelectors';
import { useChartContextStore } from 'src/zustandStores/useChartContextStore';
import type { ContextType } from 'src/utils/chartContext/types';

export interface UseAskAdvisorActionOptions {
  /**
   * Optional filter metadata (category, subcategory, dateRange, etc.)
   */
  filterMetadata?: Record<string, unknown>;
  /**
   * Optional description of the context to send to advisor
   */
  description?: string;
  /**
   * Optional context group to scope the chart payload.
   * When provided, only charts registered under this group
   * will be included in the Advisor payload.
   */
  contextGroup?: string;
  /**
   * Optional callback when context is successfully created
   */
  onContextCreated?: (contextId: string) => void;
  /**
   * Optional callback when an error occurs
   */
  onError?: (error: Error) => void;
}

/**
 * Hook that encapsulates the "Ask Advisor" action for charts in Atlas.
 * Sends chart context to zenith-service and opens Advisor in a new tab.
 *
 * Returns:
 * - `handleAskAdvisor`: Async function to trigger the action
 * - `isLoading`: Whether the action is currently in progress
 * - `shouldShow`: Whether the Ask Advisor option should be visible
 *   (based on atlas-chart-integration feature flag)
 *
 * Chart Context Flow:
 * 1. Chart components register their Highcharts options via useRegisterChartContext hook
 * 2. This hook reads from the Zustand store to get all registered chart contexts
 * 3. Full Highcharts options are sent as-is (no minification) to DynamoDB
 * 4. Context is sent to zenith-service and Advisor is opened with the context ID
 * 5. The Strands agent accesses chart data via tools, not inlined in prompt
 */
export const useAskAdvisorAction = ({
  filterMetadata,
  description,
  contextGroup,
  onContextCreated,
  onError
}: UseAskAdvisorActionOptions = {}) => {
  const [isLoading, setIsLoading] = useState(false);

  // Get chart context from Zustand store (registered by chart components)
  const getChartPayload = useChartContextStore((state) => state.getPayload);

  // Get user email from session for ownership validation
  const userEmail = useUserEmail();

  // Get current page filter context from Redux
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const retailer = useAppSelector((state) => state.retailer);
  const mainEntity = useAppSelector((state) => state.entityService?.mainEntity);
  const filters = useAppSelector((state) => state.filters);

  // Check if Ask Advisor should be shown.
  // Requires both the global feature flag AND a valid contextGroup so that
  // the button never appears on pages/sections that haven't wired up chart
  // context registration (which would result in empty metadata).
  // Business unit pages are excluded because Ask Advisor is not supported for that entity type.
  const shouldShow =
    shouldShowAtlasChartIntegration() && !!contextGroup && mainEntity?.type !== 'businessunit';

  /**
   * Trigger the Ask Advisor action.
   *
   * @param screenshotPromise - Optional promise resolving to a WebP blob
   *   captured from the chart section. Accepting a Promise (rather than a
   *   resolved Blob) allows the presigned URL fetch to run in parallel with
   *   the DOM screenshot capture, saving ~200ms of latency.
   *
   *   When provided, the screenshot is uploaded to S3 via presigned URL and
   *   both the S3 key and CloudFront viewable URL are stored in DynamoDB
   *   as an attachment on the context. Screenshot upload failure does NOT
   *   block context creation (graceful degradation).
   *
   * @param chartTitle - Optional visible title extracted from the chart
   *   section DOM (e.g. "Total Traffic by Keyword"). Stored in
   *   `appMetadata.title` for Advisor display and agent context.
   *
   * @param chartSubtitle - Optional visible subtitle extracted from the
   *   chart section DOM (e.g. "All Categories"). Stored in
   *   `appMetadata.subtitle` for Advisor display and agent context.
   */
  const handleAskAdvisor = useCallback(async (
    screenshotPromise?: Promise<Blob>,
    chartTitle?: string,
    chartSubtitle?: string
  ) => {
    if (isLoading) return;

    // Validate userEmail is available
    if (!userEmail) {
      console.error('[useAskAdvisorAction] User email not available from session');
      if (onError) {
        onError(new Error('User email not available. Please ensure you are logged in.'));
      }
      return;
    }

    setIsLoading(true);

    try {
      // Extract filter IDs from mainEntity + Atlas filter modal (Redux state.filters)
      const { retailerIds, categoryIds, subcategoryIds, segments } =
        extractAdvisorFilterIds(retailer, mainEntity, filters);

      const defaultFilterMetadata: Record<string, unknown> = {
        retailerIds,
        categoryIds,
        subcategoryIds,
        segments,
        // Pass the main entity so the agent knows what entity level this chart
        // is about (brand, category, company, business unit, segment, etc.)
        mainEntity: mainEntity ? { id: mainEntity.id, name: mainEntity.name, type: mainEntity.type } : undefined,
        mainTimePeriod: {
          displayName: mainTimePeriod?.displayName,
          startWeek: mainTimePeriod?.startWeek,
          endWeek: mainTimePeriod?.endWeek
        },
        comparisonTimePeriod: {
          displayName: comparisonTimePeriod?.displayName,
          startWeek: comparisonTimePeriod?.startWeek,
          endWeek: comparisonTimePeriod?.endWeek
        },
        ...filterMetadata
      };

      // Get chart context from Zustand store, scoped to context group if provided
      const chartPayload = getChartPayload(contextGroup);

      // Build appMetadata with contextType based on chart count.
      // Also inject the visible title/subtitle extracted from the DOM so that
      // Advisor can display them and the agent has richer context.
      let appMetadata: Record<string, unknown> | undefined;
      if (chartPayload) {
        const chartCount = chartPayload.charts?.length || 0;
        const contextType: ContextType = chartCount > 1 ? 'multiple_charts' : 'single_chart';

        appMetadata = {
          ...(chartPayload as unknown as Record<string, unknown>),
          contextType,
          ...(chartTitle ? { title: chartTitle } : {}),
          ...(chartSubtitle ? { subtitle: chartSubtitle } : {})
        };
      }

      // Log chart context info for debugging
      console.log('[useAskAdvisorAction] Chart context:', chartPayload?.charts?.length || 0, 'charts');

      // Upload screenshot to S3 if provided (graceful degradation on failure).
      //
      // Optimization: The presigned URL fetch and screenshot capture run in
      // parallel via Promise.all. The presigned URL only needs filename + mimeType
      // (not the actual blob), so we can start the network call while the DOM
      // rendering + PNG→WebP conversion is still in progress.
      //
      // Timeline:
      //   [captureElementAsWebp]  ──┐
      //      ~300-500ms              ├──► [uploadFile] → [createContext]
      //   [getUploadUrl]  ──────────┘
      //      ~200ms (overlapped)
      const attachments: AttachmentInfo[] = [];
      // contextId from upload-url response — passed to createContext so the
      // DynamoDB record uses the same ID as the S3 key of the uploaded file.
      let uploadContextId: string | undefined;

      if (screenshotPromise) {
        try {
          const screenshotFilename = `chart-screenshot-${contextGroup || 'section'}.webp`;
          const mimeType = 'image/webp';

          // 1. Run presigned URL fetch and screenshot capture in parallel
          const [screenshot, { contextId: uploadCtxId, uploadUrl, attachmentKey, viewableUrl }] = await Promise.all([
            screenshotPromise,
            advisorContextService.getUploadUrl(screenshotFilename, mimeType, 'atlas'),
          ]);

          uploadContextId = uploadCtxId;

          // 2. Upload WebP blob to S3 via presigned URL
          await advisorContextService.uploadFile(uploadUrl, screenshot, mimeType);

          // 3. Build attachment with both S3 key and CloudFront URL for DynamoDB
          attachments.push({
            type: 'screenshot',
            key: attachmentKey,
            filename: screenshotFilename,
            mimeType,
            url: viewableUrl,
          });

          console.log('[useAskAdvisorAction] Screenshot uploaded:', attachmentKey);
        } catch (uploadError) {
          // Screenshot upload failure should NOT block context creation
          console.warn('[useAskAdvisorAction] Screenshot upload failed, proceeding without:', uploadError);
        }
      }

      // Build the context request.
      // When a screenshot was uploaded, pass the contextId from the upload-url
      // response so the DynamoDB record shares the same ID as the S3 key.
      const contextRequest: CreateContextRequest = {
        ...(uploadContextId ? { contextId: uploadContextId } : {}),
        source: 'atlas',
        pageUrl: window.location.href,
        userEmail,
        description,
        filterMetadata: defaultFilterMetadata,
        appMetadata,
        attachments: attachments.length > 0 ? attachments : undefined
      };

      // Create context in zenith-service (this uses Bearer token internally)
      const response = await advisorContextService.createContext(contextRequest);

      console.log('[useAskAdvisorAction] Context created:', response.contextId);

      if (onContextCreated) {
        onContextCreated(response.contextId);
      }

      // Open Advisor with the context ID
      const advisorUrl = buildAdvisorUrl({ contextId: response.contextId });
      window.open(advisorUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('[useAskAdvisorAction] Failed to create context:', error);
      if (onError) {
        onError(error instanceof Error ? error : new Error(String(error)));
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    isLoading,
    userEmail,
    retailer,
    mainTimePeriod,
    comparisonTimePeriod,
    mainEntity,
    filters,
    filterMetadata,
    description,
    contextGroup,
    onContextCreated,
    onError,
    getChartPayload
  ]);

  return { handleAskAdvisor, isLoading, shouldShow };
};
